/**
 * Editorial comparison configs for ProductVsComparisonPage.
 *
 * Product identity, images, verified specs, and live offers are loaded from the
 * modern catalog / pricing path. This module only holds founder-approved copy
 * and the product slug set.
 *
 * Verified manufacturer numbers come from productLiveScopeSpecs /
 * productWeightSpecs / productHdPackageSpecs, not from this file.
 */

export const COMPARE_HUB_PATH = '/compare';
export const COMPARE_HUB_DOCUMENT_TITLE =
  'Compare Garmin LiveScope Options | Angler Built';
export const COMPARE_HUB_META_DESCRIPTION =
  'Compare Garmin LiveScope systems side by side, including range, coverage, target separation, hardware, and current retailer pricing from Angler Built.';

export const COMPARISON_KIND = {
  CHOICE: 'choice',
  UPGRADE: 'upgrade',
};

export const LVS42HD_VS_LVS44_COMPARISON = {
  id: 'lvs42hd-vs-lvs44',
  kind: COMPARISON_KIND.CHOICE,
  path: '/comparisons/lvs42hd-vs-lvs44',
  hubTitle: 'LiveScope 2 Comparison',
  hubSummary:
    'Choose by range first, then compare the HD versions for boat or ice.',
  documentTitle: 'Garmin LVS42HD vs LVS44 with LVS42HD-IF | Angler Built',
  metaDescription:
    'Garmin LVS42HD vs LVS44, including LVS42HD-IF: choose HD detail or farther range, then the right HD package for boat or ice.',
  pageTitle: 'Garmin LiveScope 2 Comparison',
  modelLine: 'LVS42HD, LVS42HD-IF, and LVS44',
  supportingLine: 'Choose by range first, then choose the right HD version for boat or ice.',
  stage1Heading: '1. Choose by how far you scan',
  stage2Heading: '2. If you choose HD, pick the right version',
  pricesHeading: 'Current best prices',
  compatibilityPath: '/compatibility',
  compatibilityHeading: 'Will this work with your Garmin?',
  compatibilityLine:
    'Select your display and LiveScope model to check compatibility before you order.',
  affiliateDisclosure:
    'Angler Built may earn a commission when you purchase through retailer links, at no additional cost to you.',
  allRetailerPricesId: 'all-retailer-prices',
  rangeDecision: {
    scaleMax: 250,
    anglerBuiltMarkerFt: 100,
    hdMaxFt: 125,
    lvs44MaxFt: 250,
    markerLabel: 'Decision point',
    markerValue: '~100 ft',
    hdPathLabel: 'LVS42HD / LVS42HD-IF',
    hdPathLabelShort: 'LVS42HD / IF',
    lvs44PathLabel: 'LVS44',
    accessibleTitle: 'Range decision for LVS42HD and LVS42HD-IF versus LVS44',
    accessibleDescription:
      'LVS42HD and LVS42HD-IF are represented to 125 ft. LVS44 is represented to 250 ft. Approximately 100 ft is the practical decision point. 100 to 125 ft is the overlap zone. Under 100 feet, choose LVS42HD or LVS42HD-IF for closer-range detail. From 100 to 125 feet, choose HD for detail or LVS44 for range headroom. Over 125 feet, choose LVS44.',
    guidanceNote:
      'The approximately 100 ft point is practical guidance. The 125 ft and 250 ft maximums are Garmin-published specifications.',
    summary: [
      {
        id: 'under-100',
        label: 'Under 100 ft',
        answer: 'Choose LVS42HD / IF for closer-range detail.',
      },
      {
        id: 'overlap',
        label: '100 to 125 ft',
        answer: 'Choose HD for detail or LVS44 for range headroom.',
        mobileAnswer: 'HD for detail. LVS44 for range headroom.',
      },
      { id: 'beyond-125', label: 'Over 125 ft', answer: 'Choose LVS44.' },
    ],
    equalSeparationNote: 'At 100 ft, Garmin publishes 12.6 in for both.',
    lowerIsBetter: 'Smaller minimum separation is better',
    illustrativePair: '4.5 in',
    pairStory: 'If you have two targets approximately 4.5 inches apart at 30 ft:',
    hdSepResult: 'Resolves the pair',
    lvs44SepResult: 'May merge the pair',
    illustrationNote:
      'Illustrative example based on Garmin-published minimum-separation specifications. Not a literal sonar screenshot.',
  },
  iceHole: {
    heading: 'Using a 6-inch ice hole?',
    sourceLabel: 'Angler Built field guidance',
    sourceNote:
      'This is Angler Built field guidance, not a Garmin-published specification.',
    summary: 'LVS44 does not fit a 6-inch hole. Use an 8-inch hole.',
    hdFitLabel: 'LVS42HD / LVS42HD-IF',
    hdFitStatus: 'Fits',
    lvs44FailStatus: 'Does not fit',
    lvs44EightStatus: 'Use for LVS44',
    scaleNote: 'Simplified fit illustration, not to scale.',
  },
  hdVariant: {
    sharedStatement: 'Same HD sonar performance. Different cable, mount, and weight.',
    skimLine:
      'Choose LVS42HD for boat, open water, or crossover. Choose LVS42HD-IF for portable ice.',
  },
  whyHeading: 'Why Angler Built uses approximately 100 ft as the decision point',
  why: [
    'Garmin positions LVS42HD for the most close-range detail and target separation, and LVS42HD-IF as the ice-oriented version of that same HD sonar path.',
    'Garmin positions LVS44 for clearer tracking at farther ranges and rates it to twice the manufacturer-rated maximum distance.',
    'Garmin publishes 3.8 in target separation at 30 ft for the HD path versus 5.7 in for LVS44, and the same 12.6 in specification at 100 ft.',
  ],
  guidanceNote:
    'The approximately 100 ft threshold is Angler Built guidance. The 125 ft and 250 ft maximums and target-separation measurements are Garmin-published specifications. Maximum range varies with water and bottom conditions.',
  productHd: {
    key: 'hd',
    slug: 'livescope-2-lvs42hd',
    shortLabel: 'LVS42HD',
    pdpPath: '/simple-products/livescope-2-lvs42hd',
    imageFallback: '/images/transducers/livescope-2-lvs42hd-hero.jpg',
    emptyOffersMessage: 'Current price unavailable',
    bestFor: 'Boat, open water, or boat and ice crossover',
  },
  productIce: {
    key: 'ice',
    slug: 'livescope-2-lvs42hd-ice',
    shortLabel: 'LVS42HD-IF',
    pdpPath: '/simple-products/livescope-2-lvs42hd-ice',
    imageFallback: '/images/transducers/livescope-2-lvs42hd-ice-hero.jpg',
    emptyOffersMessage: 'Current price unavailable',
    bestFor: 'Portable ice fishing',
  },
  productLvs44: {
    key: 'lvs44',
    slug: 'livescope-2-lvs44',
    shortLabel: 'LVS44',
    pdpPath: '/simple-products/livescope-2-lvs44',
    imageFallback: '/images/transducers/livescope-2-lvs44-hero.jpg',
    emptyOffersMessage: 'Current price unavailable',
    bestFor: 'Farther-range scanning',
  },
};

export const LVS34_VS_LVS42HD_UPGRADE = {
  id: 'lvs34-vs-lvs42hd',
  kind: COMPARISON_KIND.UPGRADE,
  path: '/comparisons/lvs34-vs-lvs42hd',
  hubTitle: 'LVS34 to LVS42HD Upgrade',
  hubSummary:
    'Already own LVS34? See whether the closer-range detail and simpler hardware path are worth the upgrade.',
  hubCta: 'Should I upgrade?',
  modelLine: 'LVS34 to LVS42HD',
  documentTitle: 'Garmin LVS34 vs LVS42HD Upgrade | Should You Upgrade? | Angler Built',
  metaDescription:
    'Already own LiveScope Plus LVS34? See what improves, what changes, and whether upgrading to LVS42HD is worth the cost.',
  pageTitle: 'Should You Upgrade from LVS34 to LVS42HD?',
  supportingLine:
    'LVS42HD gives you better close-range detail, much wider coverage, and no GLS 10 black box, but you give up maximum range and your current Garmin screen must be compatible.',
  ownedLabel: 'You own',
  consideringLabel: 'Considering',
  compatibilityPath: '/compatibility',
  compatibilityHeading: 'Will LVS42HD work with your current Garmin screen?',
  compatibilityLine:
    'LiveScope 2 does not work with every Garmin screen that can run LVS34. Check your current screen before upgrading.',
  compatibilityCta: 'Check Your Screen',
  garminCloserRangeClaim:
    'Garmin says LVS42HD delivers up to 50% more detail at closer ranges compared with the previous generation.',
  garminClaimLabel: 'Garmin claim',
  affiliateDisclosure:
    'Angler Built may earn a commission when you purchase through retailer links, at no additional cost to you.',
  allRetailerPricesId: 'upgrade-retailer-prices',
  ownedProduct: {
    key: 'owned',
    slug: 'garmin-lvs34',
    offerSlug: null,
    shortLabel: 'LVS34',
    pdpPath: '/simple-products/livescope-plus-lvs34',
    imageFallback: '/images/transducers/livescope-plus-lvs34.jpg',
  },
  consideredProduct: {
    key: 'considered',
    slug: 'livescope-2-lvs42hd',
    offerSlug: 'livescope-2-lvs42hd',
    shortLabel: 'LVS42HD',
    pdpPath: '/simple-products/livescope-2-lvs42hd',
    imageFallback: '/images/transducers/livescope-2-lvs42hd-hero.jpg',
    emptyOffersMessage: 'Current price unavailable',
  },
};

export const LVS34_VS_LVS44_UPGRADE = {
  id: 'lvs34-vs-lvs44',
  kind: COMPARISON_KIND.UPGRADE,
  path: '/comparisons/lvs34-vs-lvs44',
  hubTitle: 'LVS34 to LVS44 Upgrade',
  hubSummary:
    'Already own LVS34? See whether 50 ft more range, wider coverage, and removing the GLS 10 are worth the upgrade.',
  hubCta: 'Should I upgrade?',
  modelLine: 'LVS34 to LVS44',
  documentTitle: 'Garmin LVS34 vs LVS44 Upgrade | Should You Upgrade? | Angler Built',
  metaDescription:
    'Already own LiveScope Plus LVS34? See whether 50 ft more range, wider coverage, and removing the GLS 10 are worth upgrading to LVS44.',
  pageTitle: 'Should You Upgrade from LVS34 to LVS44?',
  supportingLine:
    'LVS44 adds 50 ft of rated range, wider coverage, and removes the GLS 10. The image improvement is real, but for an LVS34 owner this is more of a range, coverage, and hardware upgrade than a dramatic close-range clarity jump.',
  ownedLabel: 'You own',
  consideringLabel: 'Considering',
  compatibilityPath: '/compatibility',
  compatibilityHeading: 'Will LVS44 work with your current Garmin screen?',
  compatibilityLine:
    'LiveScope 2 does not work with every Garmin screen. Check your current screen before upgrading.',
  compatibilityCta: 'Check Your Screen',
  affiliateDisclosure:
    'Angler Built may earn a commission when you purchase through retailer links, at no additional cost to you.',
  allRetailerPricesId: 'upgrade-retailer-prices',
  ownedProduct: {
    key: 'owned',
    slug: 'garmin-lvs34',
    offerSlug: null,
    shortLabel: 'LVS34',
    pdpPath: '/simple-products/livescope-plus-lvs34',
    imageFallback: '/images/transducers/livescope-plus-lvs34.jpg',
  },
  consideredProduct: {
    key: 'considered',
    slug: 'livescope-2-lvs44',
    offerSlug: 'livescope-2-lvs44',
    shortLabel: 'LVS44',
    pdpPath: '/simple-products/livescope-2-lvs44',
    imageFallback: '/images/transducers/livescope-2-lvs44-hero.jpg',
    emptyOffersMessage: 'Current price unavailable',
  },
};

export const COMPARISON_PAGES = [
  LVS42HD_VS_LVS44_COMPARISON,
  LVS34_VS_LVS42HD_UPGRADE,
  LVS34_VS_LVS44_UPGRADE,
];

export function getComparisonById(id) {
  return COMPARISON_PAGES.find((page) => page.id === id) || null;
}

export function getComparisonByPath(path) {
  if (!path) return null;
  const normalized = path.startsWith('/') ? path : `/${path}`;
  return COMPARISON_PAGES.find((page) => page.path === normalized) || null;
}

export function getComparisonProducts(config) {
  if (!config) return [];
  if (config.kind === COMPARISON_KIND.UPGRADE) {
    return [config.ownedProduct, config.consideredProduct].filter(Boolean);
  }
  return [config.productHd, config.productIce, config.productLvs44].filter(Boolean);
}
